import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import svc from "@/services/projects.service";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";

const projectListCache = createScopedCache("projects:list");
const projectDetailCache = createScopedCache("projects:detail");
const projectReportCache = createScopedCache("projects:report");

const PROJECTS_LIST_TTL = 2 * 60 * 1000;
const PROJECTS_DETAIL_TTL = 5 * 60 * 1000;
const PROJECTS_REPORT_TTL = 90 * 1000;

export const useProjectStore = defineStore("project", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const loading = ref(false);
  const error = ref(null);
  const fetchingOne = ref(false);

  const reportLoading = ref(false);
  const reportSummary = ref({
    total_projects: 0,
    completed_projects: 0,
    pending_projects: 0,
    new_projects: 0,
  });
  const tasksChart = ref({
    labels: ["Pending", "On Hold", "In Progress", "Completed"],
    series: [0, 0, 0, 0],
  });

  // NEW flags for UI spinners
  const savingCreate = ref(false);
  const savingUpdate = ref(false);
  const deletingId = ref(null);
  const bulkDeleting = ref(false);

  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = reactive({
    q: "",
    status: "", // Active|Inactive|""
    priority: "", // High|Medium|Low|""
    sort: "recent", // recent|asc|desc
    date_from: "",
    date_to: "",
    scope: "", 
    page: 1,
    per_page: 25,
  });

  function formatDateYYYYMMDD(value) {
    if (!value) return null;
    // Accepts "2025-11-18T00:00:00.000000Z" or "2025-11-18"
    const d = new Date(value);
    if (isNaN(d)) return String(value).slice(0, 10); // fallback
    return d.toISOString().slice(0, 10);
  }

  function stripHtml(value = "") {
    if (!value) return "";
    return String(value)
      .replace(/<\/?[^>]+(>|$)/g, "") // remove tags like <p>, <br>, etc.
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .trim();
  }

  function normalize(raw = {}) {
    const leaderFirst = raw.leader?.first_name || "";
    const leaderLast = raw.leader?.last_name || "";
    const leaderFull = [leaderFirst, leaderLast].filter(Boolean).join(" ");

    const members = Array.isArray(raw.members) ? raw.members : [];
    const teamAvatars = members
      .slice(0, 3)
      .map((m) => m.avatar_url || m.avatar || null);
    const extraCount = Math.max(0, members.length - 3);

    const endRaw = raw.end_date || raw.deadline || null;
    const deadlinePretty = formatDateYYYYMMDD(endRaw);

    return {
      id: raw.id,
      project_code: raw.project_code || raw.code || raw.Project_ID || "-",
      name: raw.name || raw.Project_Name || "-",

      client_id: raw.client_id ?? raw.client?.id ?? null,
      leader_id: raw.leader_id ?? raw.leader?.id ?? null,
      member_ids: members.map((m) => m.id),

      client_name: raw.client_name || raw.client?.name || "",
      leader_name: leaderFull || raw.leader_name || "",
      leader_avatar: raw.leader_avatar || raw.leader?.avatar_url || "",

      team_avatars: teamAvatars,
      extra_team_count: extraCount,

      start_date: raw.start_date || null,
      end_date: raw.end_date || null,
      deadline: deadlinePretty,

      priority: raw.priority || "Medium",
      status: raw.status || "Active",
      value: Number(raw.value ?? 0),

      total_hours: raw.total_hours || "",
      extra_time: raw.extra_time || "",
      description: raw.description || "",
      description_plain: stripHtml(raw.description || ""),

      // 👇 NEW: task counts (support both alias or default tasks_count)
      total_tasks: raw.total_tasks ?? raw.tasks_count ?? null,
      completed_tasks: raw.completed_tasks ?? raw.tasks_completed ?? null,

      created_at: raw.created_at || null,
      updated_at: raw.updated_at || null,
    };
  }

  async function list(extra = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters, ...extra };
      const append = !!extra.append;
      if (append) {
        const { data } = await svc.list(params);
        const arr = data?.data || data?.items || [];
        const normalized = arr.map(normalize);
        const existingIds = new Set(items.value.map((x) => x.id));

        normalized.forEach((n) => {
          byId.value[n.id] = n;
        });

        const toAdd = normalized.filter((n) => !existingIds.has(n.id));
        items.value = items.value.concat(toAdd);
        meta.value = {
          total: data?.meta?.total ?? arr.length,
          per_page: data?.meta?.per_page ?? params.per_page ?? 25,
          current_page: data?.meta?.current_page ?? params.page ?? 1,
        };
      } else {
        await projectListCache.get(params, {
          ttlMs: PROJECTS_LIST_TTL,
          force: extra.force === true,
          loader: async () => {
            const { data } = await svc.list(params);
            const arr = data?.data || data?.items || [];
            return {
              items: arr.map(normalize),
              meta: {
                total: data?.meta?.total ?? arr.length,
                per_page: data?.meta?.per_page ?? params.per_page ?? 25,
                current_page: data?.meta?.current_page ?? params.page ?? 1,
              },
            };
          },
          onHydrate: ({ items: rows, meta: pageMeta }) => {
            items.value = rows || [];
            byId.value = Object.create(null);
            items.value.forEach((n) => {
              byId.value[n.id] = n;
            });
            meta.value = pageMeta || meta.value;
          },
        });
      }

      filters.page = meta.value.current_page;
      filters.per_page = meta.value.per_page;
    } catch (e) {
      error.value =
        e?.response?.data?.message || e.message || "Failed to load projects";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(extra = {}) {
    return await list({ ...extra, force: false });
  }

  async function get(id, options = {}) {
    fetchingOne.value = true;
    error.value = null;
    try {
      const payload = await projectDetailCache.get(
        { id: Number(id) || id },
        {
          ttlMs: PROJECTS_DETAIL_TTL,
          force: options.force === true,
          loader: async () => {
            const { data } = await svc.get(id);
            return { project: normalize(data?.data || data) };
          },
          onHydrate: ({ project }) => {
            if (!project) return;
            byId.value[project.id] = project;
            const i = items.value.findIndex((x) => x.id === project.id);
            if (i === -1) items.value.unshift(project);
            else items.value.splice(i, 1, project);
          },
        }
      );
      return payload.project || null;
    } catch (e) {
      error.value =
        e?.response?.data?.message || e.message || "Failed to get project";
      toast(error.value, "error");
      throw e;
    } finally {
      fetchingOne.value = false;
    }
  }

  async function ensureOne(id, options = {}) {
    return await get(id, { ...options, force: false });
  }

  async function create(payload) {
    const { project_code, ...rest } = payload || {};
    savingCreate.value = true;
    try {
      const { data } = await svc.create(rest);
      const n = normalize(data?.data || data);
      invalidateProjectCaches();
      byId.value[n.id] = n;
      items.value.unshift(n);
      toast("Project created", "success");
      return n;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Create failed";
      toast(msg, "error");
      throw e;
    } finally {
      savingCreate.value = false;
    }
  }

  async function update(id, payload) {
    savingUpdate.value = true;
    try {
      const { data } = await svc.update(id, payload);
      const n = normalize(data?.data || data);
      invalidateProjectCaches(id);
      byId.value[n.id] = n;
      const i = items.value.findIndex((x) => x.id === n.id);
      if (i === -1) items.value.unshift(n);
      else items.value.splice(i, 1, n);
      toast("Project updated", "success");
      return n;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Update failed";
      toast(msg, "error");
      throw e;
    } finally {
      savingUpdate.value = false;
    }
  }

  async function remove(id) {
    if (!id) return;
    deletingId.value = id;
    try {
      await svc.remove(id);
      invalidateProjectCaches(id);
      items.value = items.value.filter((x) => x.id !== id);
      delete byId.value[id];
      toast("Deleted", "success");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Delete failed";
      toast(msg, "error");
      throw e;
    } finally {
      deletingId.value = null;
    }
  }

  async function bulkDelete(ids = []) {
    if (!ids?.length) return;
    bulkDeleting.value = true;
    try {
      await svc.bulkDelete(ids);
      invalidateProjectCaches();
      items.value = items.value.filter((x) => !ids.includes(x.id));
      ids.forEach((id) => delete byId.value[id]);
      toast("Deleted", "success");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Delete failed";
      toast(msg, "error");
      throw e;
    } finally {
      bulkDeleting.value = false;
    }
  }

  // ---------- NEW: fetch report stats for Project Report page ----------
  async function fetchReport(extra = {}) {
    reportLoading.value = true;
    try {
      const params = { ...filters, ...extra };
      await projectReportCache.get(params, {
        ttlMs: PROJECTS_REPORT_TTL,
        force: extra.force === true,
        loader: async () => {
          const { data } = await svc.report(params);
          return {
            summary: {
              total_projects: data?.summary?.total_projects ?? 0,
              completed_projects: data?.summary?.completed_projects ?? 0,
              pending_projects: data?.summary?.pending_projects ?? 0,
              new_projects: data?.summary?.new_projects ?? 0,
            },
            tasks_chart: {
              labels: data?.tasks_chart?.labels || [
                "Pending",
                "On Hold",
                "In Progress",
                "Completed",
              ],
              series: data?.tasks_chart?.series || [0, 0, 0, 0],
            },
          };
        },
        onHydrate: ({ summary, tasks_chart }) => {
          reportSummary.value = summary || reportSummary.value;
          tasksChart.value = tasks_chart || tasksChart.value;
        },
      });
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to load report";
      toast(msg, "error");
      throw e;
    } finally {
      reportLoading.value = false;
    }
  }

  async function ensureReport(extra = {}) {
    return await fetchReport({ ...extra, force: false });
  }


  async function downloadReport(format = "xlsx") {
  try {
    const params = { ...filters };
    const res = await svc.exportReport(format, params);

    const mime =
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const blob = new Blob([res.data], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const ext = format === "pdf" ? "pdf" : "xlsx";
    link.setAttribute(
      "download",
      `project-report-${new Date().toISOString().slice(0, 10)}.${ext}`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    const msg =
      e?.response?.data?.message || e.message || "Failed to export report";
    toast(msg, "error");
    throw e;
  }
}


  const hasItems = computed(() => items.value.length > 0);

  function invalidateProjectCaches(id = null) {
    projectListCache.clear();
    projectReportCache.clear();
    if (id != null) {
      projectDetailCache.invalidate({ id: Number(id) || id });
    } else {
      projectDetailCache.clear();
    }
  }

  return {
    items,
    byId,
    loading,
    reportLoading,
    error,
    meta,
    filters,
    hasItems,
    reportSummary,
    tasksChart,
    savingCreate,
    savingUpdate,
    deletingId,
    bulkDeleting,
    list,
    ensureList,
    fetchReport,
    ensureReport,
    get,
    ensureOne,
    create,
    update,
    remove,
    bulkDelete,
    downloadReport,
    invalidateProjectCaches,
  };
});
